import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import StatsCard from '@/pages/dashboard/components/StatsCard';
import { useDashboardStats, useStockMovement, useRecentTransactions } from '@/hooks/useQueries';
import { getUserData } from '@/services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface RecentActivity {
  id: number;
  type: 'receive' | 'issue' | 'adjust';
  reference_number: string;
  location?: {
    id: number;
    name: string;
  };
  to_location_id?: number | null;
  to_location?: {
    id: number;
    name: string;
  } | null;
  to_patient_id?: number | null;
  to_patient?: {
    id: number;
    name: string;
  } | null;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  items: Array<{
    id: number;
    item: {
      id: number;
      name: string;
    };
    quantity: number;
  }>;
  created_at: string;
}

export default function Dashboard() {
  const [showAppModal, setShowAppModal] = useState(false);
  const navigate = useNavigate();
  const isAdmin = getUserData()?.role === 'admin';
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [stats, setStats] = useState<{
    totalItems: number;
    lowStockItems: number;
  }>({
    totalItems: 0,
    lowStockItems: 0,
  });
  const [stockMovementData, setStockMovementData] = useState<Array<{
    day: string;
    dayName: string;
    date: string;
    received: number;
    issued: number;
    receivedItems: Array<{ item_name: string; quantity: number }>;
    issuedItems: Array<{ item_name: string; quantity: number }>;
  }>>([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingMovement, setLoadingMovement] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Calculate the week containing the selected date (Monday to Sunday)
  const getWeekForDate = (date: Date): { start: Date; end: Date } => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    
    // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = d.getDay();
    
    // Calculate days to subtract to get to Monday (if Sunday, go back 6 days, otherwise go back dayOfWeek - 1)
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    
    const start = new Date(d);
    start.setDate(start.getDate() - daysToMonday);
    
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    
    return { start, end };
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Calculate days based on selected date's week or default to last 7 days
  const calculateDays = () => {
    if (selectedDate) {
      const weekRange = getWeekForDate(selectedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const startDate = new Date(weekRange.start);
      startDate.setHours(0, 0, 0, 0);
      const diffTime = today.getTime() - startDate.getTime();
      let days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 7; // Add 7 to include the full week
      if (days < 7) days = 7;
      if (days > 365) days = 365; // Cap at 1 year
      return days;
    }
    return 7;
  };

  // Use React Query hooks for data fetching
  const { data: statsData, isLoading: statsLoading } = useDashboardStats();
  const days = calculateDays();
  const { data: movementData, isLoading: movementLoading } = useStockMovement(days);
  const { data: recentTransactionsData, isLoading: transactionsLoading } = useRecentTransactions(10);

  // Update local state from React Query data
  useEffect(() => {
    if (statsData?.overview) {
      setStats({
        totalItems: statsData.overview.total_items || 0,
        lowStockItems: statsData.overview.low_stock_items || 0,
      });
    }
    setLoadingStats(statsLoading);
  }, [statsData, statsLoading]);

  // Process stock movement data when it changes (React Query handles fetching)
  useEffect(() => {
    setLoadingMovement(movementLoading);
    
    // Get date range for chart display
    let startDate: Date;
    let endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    
    if (selectedDate) {
      const weekRange = getWeekForDate(selectedDate);
      startDate = new Date(weekRange.start);
      endDate = new Date(weekRange.end);
      endDate.setHours(23, 59, 59, 999);
    } else {
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 6);
      startDate.setHours(0, 0, 0, 0);
    }
    
    // Generate all dates in the range
    const dateRange: Array<{ date: Date; dayName: string; dateString: string; formattedDate: string }> = [];
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateString = currentDate.toISOString().split('T')[0];
      const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'short' });
      const formattedDate = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dateRange.push({ date: new Date(currentDate), dayName, dateString, formattedDate });
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    if (Array.isArray(movementData) && movementData.length > 0) {
      // Key the map by the unique ISO dateString, not by dayName.
      // Using dayName as a key causes data from same-named days across multiple
      // weeks (e.g. two Mondays) to be merged/summed incorrectly.
      const dateMap = new Map<string, { 
        received: number; 
        issued: number;
        receivedItems: Array<{ item_name: string; quantity: number }>;
        issuedItems: Array<{ item_name: string; quantity: number }>;
      }>();
      
      movementData.forEach((item: any) => {
        const dateStr = item.date || item.created_at?.split('T')[0];
        if (!dateStr) return;
        
        const matchingDay = dateRange.find(d => d.dateString === dateStr);
        if (!matchingDay) return;
        
        const key = matchingDay.dateString; 
        if (!dateMap.has(key)) {
          dateMap.set(key, { received: 0, issued: 0, receivedItems: [], issuedItems: [] });
        }
        
        const dayData = dateMap.get(key)!;
        if (item.type === 'receive') {
          dayData.received += item.total_items || 0;
          if (item.items && Array.isArray(item.items)) {
            dayData.receivedItems.push(...item.items.map((i: any) => ({
              item_name: i.item_name,
              quantity: i.quantity
            })));
          }
        } else if (item.type === 'issue') {
          dayData.issued += item.total_items || 0;
          if (item.items && Array.isArray(item.items)) {
            dayData.issuedItems.push(...item.items.map((i: any) => ({
              item_name: i.item_name,
              quantity: i.quantity
            })));
          }
        }
      });
      
      // Convert to chart format — look up by dateString, not dayName
      const chartData = dateRange.map(({ dateString, dayName, formattedDate }) => {
        const dayData = dateMap.get(dateString) || { received: 0, issued: 0, receivedItems: [], issuedItems: [] };
        return {
          day: `${dayName} ${formattedDate}`,
          dayName,
          date: formattedDate,
          received: dayData.received,
          issued: dayData.issued,
          receivedItems: dayData.receivedItems,
          issuedItems: dayData.issuedItems,
        };
      });
      
      setStockMovementData(chartData);
    } else {
      // Empty chart
      setStockMovementData(dateRange.map(({ dayName, formattedDate }) => ({ 
        day: `${dayName} ${formattedDate}`,
        dayName,
        date: formattedDate,
        received: 0, 
        issued: 0,
        receivedItems: [],
        issuedItems: []
      })));
    }
  }, [movementData, movementLoading, selectedDate]);

  // Update recent activities from React Query
  useEffect(() => {
    setLoadingActivities(transactionsLoading);
    if (Array.isArray(recentTransactionsData)) {
      setRecentActivities(recentTransactionsData);
    } else {
      setRecentActivities([]);
    }
  }, [recentTransactionsData, transactionsLoading]);

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <style>{`
        .dashboard-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .dashboard-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 3px;
        }
        .dashboard-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .dashboard-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
      <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-5 px-4 sm:px-6 lg:pr-4">
        {/* Header */}
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">Monitor inventory status and activities</p>
        </div>

        {/* Stats Grid */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="w-full sm:w-48 flex-1 sm:flex-none min-w-[200px]">
            <StatsCard
              title="Total Products"
              value={loadingStats ? '...' : stats.totalItems.toLocaleString()}
            />
          </div>
          <div className="w-full sm:w-48 flex-1 sm:flex-none min-w-[200px]">
            <StatsCard
              title="Low Stock Items"
              value={loadingStats ? '...' : stats.lowStockItems.toLocaleString()}
            />
          </div>
        </div>

        {/* Charts and Recent Activity Section */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-5">
          {/* Stock Movement Chart */}
          <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-5 flex-shrink-0">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Stock Movement</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Daily received and issued items</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full sm:w-auto flex justify-center sm:justify-start">
                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer border border-gray-300"
                    title="Select a date to view its week"
                  >
                    <i className="ri-calendar-line text-sm"></i>
                    {selectedDate ? (
                      <>
                        <span className="hidden sm:inline">
                          Week of {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="sm:hidden">
                          {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </>
                    ) : (
                      <span>Last 7 Days</span>
                    )}
                  </button>
                  
                  {showDatePicker && (
                    <div ref={datePickerRef} className="absolute left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 sm:p-4 z-50 w-[calc(100vw-3rem)] sm:w-auto sm:min-w-[280px] max-w-[280px] sm:max-w-sm">
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-xs sm:text-sm font-semibold text-gray-900">Select Date</h3>
                          <button
                            onClick={() => setShowDatePicker(false)}
                            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                            aria-label="Close"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        </div>
                        
                        <div>
                          <p className="text-xs text-gray-600 mb-2">
                            <span className="hidden sm:inline">Select a date to view its week (Monday - Sunday)</span>
                            <span className="sm:hidden">Select a date to view its week</span>
                          </p>
                          <input
                            type="date"
                            value={selectedDate ? selectedDate.toISOString().split('T')[0] : ''}
                            onChange={(e) => {
                              if (e.target.value) {
                                const date = new Date(e.target.value);
                                if (date <= new Date()) {
                                  setSelectedDate(date);
                                  setShowDatePicker(false);
                                }
                              }
                            }}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
                          />
                        </div>
                        
                        <button
                          onClick={() => {
                            setSelectedDate(null);
                            setShowDatePicker(false);
                          }}
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          <span className="hidden sm:inline">Reset to Default (Last 7 Days)</span>
                          <span className="sm:hidden">Reset</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/reports')}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-[#eb1539] hover:bg-[#eb1539]/10 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="hidden sm:inline">View All Reports</span>
                    <span className="sm:hidden">Reports</span>
                    <i className="ri-arrow-right-line text-sm"></i>
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-[400px] min-h-[250px] sm:min-h-[280px] overflow-auto flex-1 dashboard-scrollbar">
              {loadingMovement ? (
                <div className="h-[300px] sm:h-[350px] flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#50b3e1]"></div>
                    <p className="mt-2 text-xs text-gray-500">Loading chart data...</p>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] sm:h-[350px] w-full min-w-0 overflow-x-auto">
                  <ResponsiveContainer width="100%" height="100%" minWidth={400}>
                    <LineChart data={stockMovementData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="day" 
                        stroke="#6b7280" 
                        style={{ fontSize: '10px' }}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval="preserveStartEnd"
                      />
                      <YAxis stroke="#6b7280" style={{ fontSize: '11px' }} />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                                <p className="font-semibold text-gray-900 mb-2">{data.dayName} {data.date}</p>
                                {data.received > 0 && (
                                  <div className="mb-2">
                                    <p className="text-sm font-medium text-[#eb1539] mb-1">
                                      Received: {data.received} product{data.received !== 1 ? 's' : ''}
                                    </p>
                                    {data.receivedItems && data.receivedItems.length > 0 && (
                                      <div className="ml-2 text-xs text-gray-600 space-y-0.5 max-h-32 overflow-y-auto">
                                        {data.receivedItems.map((item: any, idx: number) => (
                                          <p key={idx}>• {item.item_name}: {item.quantity}</p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {data.issued > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-[#0997a4] mb-1">
                                      Issued: {data.issued} product{data.issued !== 1 ? 's' : ''}
                                    </p>
                                    {data.issuedItems && data.issuedItems.length > 0 && (
                                      <div className="ml-2 text-xs text-gray-600 space-y-0.5 max-h-32 overflow-y-auto">
                                        {data.issuedItems.map((item: any, idx: number) => (
                                          <p key={idx}>• {item.item_name}: {item.quantity}</p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line type="monotone" dataKey="received" stroke="#eb1539" strokeWidth={2} name="Received" dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="issued" stroke="#0997a4" strokeWidth={2} name="Issued" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col">
            <div className="flex items-center justify-between mb-4 sm:mb-5 flex-shrink-0">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h2>
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1 sm:pr-2 flex-1 dashboard-scrollbar">
              {loadingActivities ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#50b3e1]"></div>
                  <p className="mt-2 text-xs text-gray-500">Loading activities...</p>
                </div>
              ) : recentActivities.length > 0 ? (
                recentActivities.map((activity) => {
                  const isReceive = activity.type === 'receive';
                  const isTransfer = activity.type === 'issue' && activity.to_location_id;
                  const isIssueToPatient = activity.type === 'issue' && activity.to_patient_id;
                  const firstItem = activity.items?.[0];
                  const totalQuantity = activity.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
                  
                  return (
                    <div key={activity.id} className="flex items-start gap-2.5 pb-3 border-b border-gray-100 last:border-0 last:pb-0 hover:bg-gray-50/50 transition-colors rounded-lg px-1">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isReceive ? 'bg-green-100' : isTransfer ? 'bg-purple-100' : 'bg-[#50b3e1]/10'
                      }`}>
                        <i className={`${
                          isReceive ? 'ri-inbox-line text-green-600' : 
                          isTransfer ? 'ri-arrow-left-right-line text-purple-600' : 
                          'ri-send-plane-line text-[#50b3e1]'
                        } text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {firstItem?.item?.name || 'Item'}
                          </p>
                          <span className={`text-xs font-semibold ${
                            isReceive ? 'text-green-600' : isTransfer ? 'text-purple-600' : 'text-[#50b3e1]'
                          }`}>
                            {isReceive ? `+${totalQuantity}` : `-${totalQuantity}`}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {isReceive ? 'Received' : 
                           isTransfer ? `Transferred to ${activity.to_location?.name || 'Unknown'}` : 
                           isIssueToPatient ? `Issued to ${activity.to_patient?.name || 'Patient'}` :
                           'Issued'} by {activity.user?.name || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {isTransfer ? `From ${activity.location?.name || 'N/A'}` : activity.location?.name || 'N/A'} • {formatTimeAgo(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-file-list-line text-gray-400 text-xl"></i>
                  </div>
                  <p className="text-sm text-gray-500">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Download Modal */}
      {showAppModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Download Mobile App</h3>
              <button
                onClick={() => setShowAppModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="text-center">
              <div className="w-40 h-40 sm:w-48 sm:h-48 mx-auto bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                <div className="text-center">
                  <i className="ri-qr-code-line text-5xl sm:text-6xl text-gray-400 mb-2"></i>
                  <p className="text-xs text-gray-500">QR Code</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mb-4">
                Scan this QR code with your mobile device to download the HIC Stock Management app
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-apple-fill text-lg"></i>
                  App Store
                </button>
                <button className="flex items-center justify-center gap-2 px-4 py-2 bg-[#eb1539] text-white rounded-lg hover:bg-[#c01030] transition-colors whitespace-nowrap cursor-pointer text-sm">
                  <i className="ri-google-play-fill text-lg"></i>
                  Play Store
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </DashboardLayout>
    </>
  );
}
